import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { Unit } from "@corpuscomplete/core/common/unit";
import { z } from "zod/v4";
import { Rmr as RmrInternal } from "./rmr";
import { withZeroTransaction } from "../../zero/utils";
import { createInsertSchema } from "../../utils/sql";
import { nutritionProgram, nutritionProgramExpenditure } from "./nutrition-program.sql";
import { MacroDistribution as MacroDistributionInternal } from "./macro-distribution";
import { Calendar } from "@corpuscomplete/core/common/calendar";
import { defineQuery } from "../../zero/transformer";
import { type ExpressionBuilder, type QueryRowType, type Transaction } from "@rocicorp/zero";
import { zql, type Schema } from "../../zero/schema";
import { BodyMetricEntry } from "@corpuscomplete/core/body/body-metric-entry";
import { BodyCharacteristic } from "@corpuscomplete/core/body/body-characteristic";
import { BodyMetricGoal } from "@corpuscomplete/core/body/body-metric-goal";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import type { UserActor } from "@corpuscomplete/core/actor";
import { accessDenied } from "@corpuscomplete/core/utils";
import { DateRange } from "@corpuscomplete/core/common/date-time";

/**
 * Nutrition-program namespace boundary for date-ranged targets and expenditure planning.
 *
 * Invariants:
 * - Programs are queried/applied as date-range intervals per user.
 * - Cross-user read/write requires matching `nutrition_programs` grants.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace NutritionProgram {
  export namespace Rmr {
    export type Parameters = RmrInternal.Parameters;
    export type Equation = RmrInternal.Equation;
  }

  export namespace MacroDistribution {
    export const suggestedMinimums = MacroDistributionInternal.suggestedMinimums;
    export const calculateNutrientTargets = MacroDistributionInternal.calculateNutrientTargets;
  }

  const CALORIES_PER_LB = 3500;
  const DAYS_PER_WEEK = 7;

  export function formatGoalSummary(goalOffset: number): string {
    if (goalOffset === 0) {
      return "maintenance";
    }
    const absOffset = Math.abs(goalOffset);
    if (goalOffset < 0) {
      return `weight loss with ${absOffset} kcal deficit`;
    }
    return `muscle gain with ${absOffset} kcal surplus`;
  }

  /**
   * Calculate daily calorie offset from weekly weight change rate.
   * Based on ~3500 calories per pound of body weight.
   *
   * @param ratePerWeek - Rate of weight change per week (positive = gain, negative = loss)
   * @returns Daily calorie offset in calories
   */
  export function calorieOffsetFromWeightChangeRate(ratePerWeek: Unit.Quantity<"mass">): number {
    const lbPerWeek = Unit.convert(ratePerWeek, "lb").quantity;
    return Math.round((lbPerWeek * CALORIES_PER_LB) / DAYS_PER_WEEK);
  }

  export const inDateRange = defineQuery(
    z.object({
      dateRange: DateRange.closedSchema,
      userId: z.uuid(),
    }),
    ({ ctx, args: { dateRange, userId } }) => {
      const q = zql.nutritionProgram
        .where((eb) => DateRange.overlapsWhere(eb, dateRange))
        .where("userId", "=", userId)
        .related("days");
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );
  export type Preview = QueryRowType<typeof inDateRange>;
  export type Day = Preview["days"][number];

  function viewable(eb: ExpressionBuilder<"nutritionProgram", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("userId", "=", actor.userId),
      eb.exists("grantsByUser", (q) =>
        AccessGrant.matching(
          q,
          actor,
          AccessGrant.criteria("user", "nutrition_programs", ["editor", "viewer"]),
        ),
      ),
    );
  }

  export const onDate = defineQuery(
    z.object({ userId: z.uuid(), date: z.iso.date() }),
    ({ ctx, args: { userId, date } }) => {
      const onDate = Temporal.PlainDate.from(date);
      const q = zql.nutritionProgram
        .where("userId", "=", userId)
        .where("dateStart", "<=", onDate)
        .where((eb) => eb.or(eb.cmp("dateEnd", "IS", null), eb.cmp("dateEnd", ">=", onDate)))
        .related("days")
        .related("expenditure")
        .one();
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );
  export type Info = NonNullable<QueryRowType<typeof onDate>>;

  export const details = (program: Info | undefined) => {
    if (!program) {
      return undefined;
    }
  };
  export type Details = ReturnType<typeof details>;

  export const dayForDate = (programs: Preview[], date: Temporal.PlainDate): Day | undefined => {
    const program = programs.find(
      (program) =>
        Temporal.PlainDate.compare(program.dateStart, date) <= 0 &&
        (program.dateEnd === null || Temporal.PlainDate.compare(program.dateEnd, date) >= 0),
    );
    if (!program) {
      return undefined;
    }

    const dayOfWeek = Calendar.asDayOfWeek(date);
    let defaultDay: Preview["days"][number] | undefined;
    for (const day of program.days) {
      if (day.dateSelector?.type === "dayOfWeek" && day.dateSelector.values.includes(dayOfWeek)) {
        return day;
      }
      if (day.dateSelector?.type === "default") {
        defaultDay = day;
      }
    }
    return defaultDay;
  };

  export const autoDaySchema = z.object({
    type: z.literal("auto"),
    dateSelector: Calendar.dateSelectorSchema,
    macroDistribution: MacroDistributionInternal.schema,
    expenditureOverrideActivityFactor: z.number().min(1.0).max(10.0).nullable(),
    expenditureAdditionalOffset: z.number().min(-10_000).max(10_000).nullable(),
  });
  export type AutoDay = z.infer<typeof autoDaySchema>;
  export const manualDaySchema = z.object({
    type: z.literal("manual"),
    dateSelector: Calendar.dateSelectorSchema,
    nutrientTargets: Nutrient.targetsSchema(),
  });
  export type ManualDay = z.infer<typeof manualDaySchema>;

  export type WeightGoalType = "maintain" | "lose" | "gain";
  export const weightGoalDefaultOffset = (type: WeightGoalType) => {
    switch (type) {
      case "lose":
        return -500;
      case "maintain":
        return 0;
      case "gain":
        return 500;
    }
  };

  async function canEdit(tx: Transaction, actor: UserActor, programId: string): Promise<boolean> {
    const program = await tx.run(zql.nutritionProgram.where("id", "=", programId).one());
    if (!program) {
      return false;
    }
    if (program.userId === actor.userId) {
      return true;
    }
    const result = await tx.run(
      zql.nutritionProgram
        .where("id", "=", programId)
        .whereExists(
          "grantsByUser",
          (r) =>
            AccessGrant.matching(
              r,
              actor,
              AccessGrant.criteria("user", "nutrition_programs", ["editor"]),
            ),
          {
            flip: true,
          },
        )
        .one(),
    );
    return !!result;
  }

  async function canAddToUser(tx: Transaction, actor: UserActor, userId: string): Promise<boolean> {
    if (actor.userId === userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", userId),
        actor,
        AccessGrant.criteria("user", "nutrition_programs", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  export const create = withZeroTransaction(
    createInsertSchema(nutritionProgram)
      .omit({ dateStart: true, dateEnd: true })
      .extend({
        dateRange: DateRange.openEndSchema,
        bodyComposition: z
          .object({
            entryId: z.uuid(),
            samples: BodyMetricEntry.BodyComposition.samplesSchema,
          })
          .nullable(),
        bodyCharacteristic: BodyCharacteristic.set.schema.omit({ userId: true }).nullable(),
        // TODO: Should consider passing in the relevant BodyMetricSamples / BodyCharacteristics separate from rmrParameters so that specific validation can be done
        // e.g. range checks (you aren't negative 5 or 700 years old), dependency checks (lean body mass should be less than weight), etc.
        // Or could just map the rmrParameters to some type that matches existing
        expenditure: createInsertSchema(nutritionProgramExpenditure)
          .omit({ programId: true, goalId: true })
          .extend({
            activityFactor: z.number().min(1.0).max(10.0),
            calibrationOffset: z.number().min(-10_000).max(10_000),
            rmrParameters: RmrInternal.parametersSchema,
            goalOffset: z.number().min(-10_000).max(10_000),
            goal: BodyMetricGoal.Weight.create.schema
              .omit({
                userId: true,
                dateStart: true,
              })
              .nullable(),
          }),
        days: z
          .union([autoDaySchema.extend({ id: z.uuid() }), manualDaySchema.extend({ id: z.uuid() })])
          .array()
          .min(1),
      })
      .check((ctx) => {
        Calendar.validateDateSelectors(ctx.value.days, ctx.issues.push);
        if (ctx.value.expenditure) {
          // TODO: Validate rmrParameter values
        } else {
          for (const { type } of ctx.value.days) {
            if (type === "auto") {
              ctx.issues.push({
                code: "custom",
                message: `Expenditure is required for days of type "auto"`,
                input: ctx.value.days,
              });
              break;
            }
          }
        }
      }),
    async ({
      tx,
      ctx,
      args: {
        id: programId,
        dateRange,
        userId,
        expenditure,
        days,
        bodyComposition,
        bodyCharacteristic,
      },
    }) => {
      const hasPermission = await canAddToUser(tx, ctx.actor, userId);
      if (!hasPermission) {
        accessDenied("You do not have permission to add a nutrition program to this user");
      }
      const timeNow = Temporal.Now.instant().epochMilliseconds;
      const { dateStart } = dateRange;
      const dateEnd = dateRange.dateEnd;

      const bodyPromises: Promise<void>[] = [];
      if (bodyComposition) {
        // TODO: This should probably just be a "setOnDate" mutator?

        const existingEntry = await tx.run(
          BodyMetricEntry.onDate.fn({
            ctx,
            args: {
              date: dateRange.dateStart.toString(),
              type: "body-composition",
              userId,
            },
          }),
        );
        if (existingEntry) {
          const promise = BodyMetricEntry.BodyComposition.updateSamples.fn({
            tx,
            ctx,
            args: {
              ...bodyComposition,
              entryId: existingEntry.id,
            },
          });
          bodyPromises.push(promise);
        } else {
          const promise = BodyMetricEntry.BodyComposition.createOnDate.fn({
            tx,
            ctx,
            args: {
              date: dateRange.dateStart.toString(),
              userId,
              ...bodyComposition,
            },
          });
          bodyPromises.push(promise);
        }
      }
      if (bodyCharacteristic) {
        const promise = BodyCharacteristic.set.fn({
          tx,
          ctx,
          args: {
            userId,
            ...bodyCharacteristic,
          },
        });
        bodyPromises.push(promise);
      }
      await Promise.all(bodyPromises);

      const existing = await tx.run(
        zql.nutritionProgram.where("userId", "=", userId).where("dateEnd", "IS", null).one(),
      );
      if (existing) {
        if (Temporal.PlainDate.compare(existing.dateStart, dateStart) === 0) {
          await del.fn({
            tx,
            ctx,
            args: { programId: existing.id },
          });
        } else {
          await tx.mutate.nutritionProgram.update({
            id: existing.id,
            dateEnd: dateStart.subtract({ days: 1 }),
            timeUpdated: timeNow,
          });
        }
      }

      await tx.mutate.nutritionProgram.insert({
        id: programId,
        dateStart,
        dateEnd,
        userId,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });

      // TODO: Authorize that target date, current weight, goal weight, and goal offset add up properly?

      if (expenditure) {
        const { goal, ...rest } = expenditure;
        if (goal) {
          await BodyMetricGoal.Weight.create.fn({
            tx,
            ctx,
            args: {
              ...goal,
              userId,
              dateStart,
            },
          });
        }
        await tx.mutate.nutritionProgramExpenditure.insert({
          programId,
          ...rest,
          goalId: goal?.id,
        });
      }

      await Promise.all(
        days.map(async (day) => {
          if (day.type === "manual") {
            await tx.mutate.nutritionProgramDay.insert({
              programId,
              userId,
              id: day.id,
              dateSelector: day.dateSelector,
              nutrientTargets: Nutrient.normalizeTargets(day.nutrientTargets),
            });
          } else {
            if (!expenditure) {
              throw new Error(
                "Invariant violation: expenditure is required for days of type 'auto'",
              );
            }
            const totalCalories = totalCaloriesForDay(expenditure, day);
            const weight = Unit.convert(expenditure.rmrParameters.weight, "kg");

            const calculatedTargets = MacroDistributionInternal.calculateNutrientTargets(
              day.macroDistribution,
              totalCalories,
              weight,
            );
            if (calculatedTargets instanceof Error) {
              throw calculatedTargets;
            }
            await tx.mutate.nutritionProgramDay.insert({
              programId,
              userId,
              nutrientTargets: Nutrient.normalizeTargets(calculatedTargets),
              id: day.id,
              dateSelector: day.dateSelector,
              macroDistribution: day.macroDistribution,
              expenditureOverrideActivityFactor: day.expenditureOverrideActivityFactor,
              expenditureAdditionalOffset: day.expenditureAdditionalOffset,
            });
          }
        }),
      );
    },
  );
  export type Create = z.infer<typeof create.schema>;

  export const totalCaloriesForDay = (
    expenditure: NonNullable<Create["expenditure"]>,
    day: AutoDay,
  ) => {
    const activityFactor = day.expenditureOverrideActivityFactor ?? expenditure.activityFactor;
    const offset =
      expenditure.calibrationOffset +
      (day.expenditureAdditionalOffset ?? 0) +
      expenditure.goalOffset;
    return activityFactor * RmrInternal.calculate(expenditure.rmrParameters) + offset;
  };

  const del = withZeroTransaction(
    z.object({ programId: z.uuid() }),
    async ({ tx, ctx, args: { programId } }) => {
      const hasPermission = await canEdit(tx, ctx.actor, programId);
      if (!hasPermission) {
        accessDenied("You do not have permission to delete this nutrition program");
      }
      const days = await tx.run(zql.nutritionProgramDay.where("programId", "=", programId));
      const daysPromise = Promise.all(
        days.map(async ({ id }) => {
          await tx.mutate.nutritionProgramDay.delete({ id });
        }),
      );
      const expenditurePromise = tx.mutate.nutritionProgramExpenditure.delete({
        programId,
      });
      await Promise.all([daysPromise, expenditurePromise]);
      await tx.mutate.nutritionProgram.delete({ id: programId });
    },
  );
}
